import { COMPANY, IMAGES, CAPABILITIES } from "@/data";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-20">
          <div className="col-span-12 lg:col-span-7">
            <div className="hu-section-tag mb-6">About Huron Truss</div>
            <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tight leading-[0.95] text-neutral-900">
              Built in the North, for the North.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-12">
            <p className="text-neutral-600 leading-relaxed text-base lg:text-lg">
              Huron Truss Ltd. is a Northern Ontario manufacturer of engineered wood components.
              Founded in 2021, we're a young company run by a team with a lot of
              experience in every aspect of the truss industry — local manufacturing with national-grade
              engineering and a small-shop work ethic.
            </p>
          </div>
        </div>

        {/* Story + image */}
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-24">
          <div className="col-span-12 lg:col-span-6">
            <img
              src={IMAGES.lumberYard}
              alt="Huron Truss lumber yard"
              className="w-full aspect-[4/3] object-cover rounded-sm border border-stone-200"
              loading="lazy"
            />
          </div>
          <div className="col-span-12 lg:col-span-6 lg:pl-6">
            <div className="hu-section-tag mb-6">Our story</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight mb-6">
              A Northern Ontario manufacturer, founded in {COMPANY.established}.
            </h2>
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              <p>
                Huron Truss was founded on a simple idea: provide a quality product while
                maintaining a high level of service at a competitive price.  Service never takes
                a back seat to company growth.
              </p>
              <p>
                We may be a young company, but the people behind it aren't new to this industry.
                Our design and production staff bring decades of combined industry experience.
              </p>
              <p>
                Today we run <strong>two manufacturing plants</strong> — Bruce Mines in the Algoma
                District and Val Gagné in the Cochrane District.  Our design team can help you 
                with a wide range of projects from custom homes, camps and cottages to commercial and farm
                buildings.
              </p>
              <p>
                What hasn't changed since day one: when you call, you talk to a person who has the
                knowledge to help you with your project.
              </p>
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div className="mb-24">
          <div className="hu-section-tag mb-6">Capabilities</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight leading-tight max-w-3xl mb-12">
            Design, manufacture, deliver — under one roof.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="bg-stone-50 p-7 lg:p-8">
                  <Icon size={32} strokeWidth={1.3} className="text-amber-700" />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2 tracking-tight">
                    {c.title}
                  </h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team */}
        <div className="mb-12">
          <div className="hu-section-tag mb-6">The team</div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight leading-tight mb-12">
            Talk to the people who'll handle your job.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
            {COMPANY.team.map((m) => (
              <div key={m.name} className="bg-stone-50 p-8 lg:p-10" data-testid={`team-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="hu-section-tag !before:hidden mb-4">{m.role}</div>
                <h3 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-neutral-900">
                  {m.name}
                </h3>
                <a
                  href={`mailto:${m.email}`}
                  className="inline-block mt-3 text-amber-700 hover:text-amber-900 text-sm font-medium"
                >
                  {m.email}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-12 mt-12 border-t border-stone-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="font-display font-bold text-2xl lg:text-3xl tracking-tight max-w-xl">
            Ready to get a quote on your next build?
          </p>
          <Link
            to="/quote"
            data-testid="about-quote-cta"
            className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-6 py-3.5 rounded-sm transition-colors self-start sm:self-auto"
          >
            Request a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}
